/** @forge-header
 * @id          mlbParsers
 * @role        MLB ticker parsers | game moneyline, player props, event markets
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo,PlayerInfo,TickerCategory | kalshiTicker/helpers:parseFallback,mlbTeamFromCode,formatPlayerName
 * @downstream  kalshiTicker/parse | stores/marketStore (classifyMlbExtendedTicker)
 * @exports     parseMlbGame:fn | parseMlbExtended:fn | classifyMlbExtendedTicker:fn | MLB_PROP_SERIES | MLB_PROP_STAT_LABELS | MLB_PROP_CATEGORIES
 * @test        parse.test.ts
 */

import type { ParsedTicker, GameInfo, PlayerInfo, TickerCategory } from '../types';
import { parseDateFromTicker, formatDateShort } from '../types';
import {
  parseFallback,
  mlbTeamFromCode,
  formatPlayerName,
  splitMlbDoubleheaderSuffix,
} from '../helpers';

/**
 * MLB game moneyline ticker. Two observed shapes:
 *   KXMLBGAME-26APR29NYYTEX-NYY              (no time)
 *   KXMLBGAME-26APR291435NYYTEX-NYY          (with HHMM time)
 *
 * Team codes are variable length (e.g. KC=2, NYY=3, ATH=3) so the trailing
 * -X (the YES team) is used as an anchor instead of fixed offsets.
 */
export function parseMlbGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 3) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 7) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  if (!date) return parseFallback(raw);

  // Strip optional HHMM time block, then the doubleheader G1/G2 suffix
  // (26JUL221305PITNYYG1 — without this the YES-team anchor never matches).
  let teamBlob = mid.slice(7);
  if (/^\d{4}/.test(teamBlob)) teamBlob = teamBlob.slice(4);
  const dh = splitMlbDoubleheaderSuffix(teamBlob);
  teamBlob = dh.teams;

  const yesTeam = parts[2] ?? '';
  if (!yesTeam) return parseFallback(raw);

  let awayCode: string;
  let homeCode: string;
  if (teamBlob.endsWith(yesTeam) && teamBlob.length > yesTeam.length) {
    awayCode = teamBlob.slice(0, -yesTeam.length);
    homeCode = yesTeam;
  } else if (teamBlob.startsWith(yesTeam) && teamBlob.length > yesTeam.length) {
    awayCode = yesTeam;
    homeCode = teamBlob.slice(yesTeam.length);
  } else {
    return parseFallback(raw);
  }

  if (!awayCode || !homeCode) return parseFallback(raw);

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: mlbTeamFromCode(awayCode) ?? undefined,
    homeName: mlbTeamFromCode(homeCode) ?? undefined,
    gameNumber: dh.gameNumber,
  };

  const dhLabel = dh.gameNumber ? ` (G${dh.gameNumber})` : '';
  const shortLabel = `MLB: ${awayCode} @ ${homeCode}${dhLabel}`;
  const fullLabel = yesTeam
    ? `MLB: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode}${dhLabel} · ${yesTeam} Win`
    : `MLB: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode}${dhLabel}`;

  return {
    raw,
    category: 'mlb-game',
    series: 'KXMLBGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: yesTeam,
    shortLabel,
    fullLabel,
  };
}

// ── MLB player props + per-game event markets ──────────────────────────
//
// New Kalshi MLB series (2026-07-14, mlb-props-events). All share the
// KXMLBGAME event core `<YY><MON><DD><HHMM><AWAY><HOME>[G1|G2]`, so they
// collapse onto the game's canonicalGameKey (incl. the doubleheader
// G-suffix). All Kalshi-only, period 'full', flow-raw on the WS feed.
//
//   Player props (yes-ladder, floor_strike = N − 0.5):
//     <event>-<TEAM><PLAYERCODE>-<N>   e.g. KXMLBHIT-26JUL121610TORSD-SDFTATIS23-2
//   Team total: <event>-<TEAM><N>      e.g. KXMLBTEAMTOTAL-…TBBOS-SD2
//   RFI: market ticker == event ticker (bare binary, YES = run in 1st inning)
//   Extras: <event>-EXTRAS             (binary, YES = game reaches extras)
//
// `marketType` on the wire is NOT authoritative for these (hydra sends none,
// the edge stamps 'game'/'prop') — consumers classify by ticker prefix via
// `classifyMlbExtendedTicker`, matching the EXACT series segment so the
// KXMLBHR/KXMLBHRR pair can't shadow each other.

/** The eight player-prop stat families, keyed by series ticker. */
export type MlbPropStat = 'hits' | 'hr' | 'rbi' | 'tb' | 'sb' | 'ks' | 'outs' | 'hrr';

export const MLB_PROP_SERIES: Record<string, MlbPropStat> = {
  KXMLBHIT: 'hits',
  KXMLBHR: 'hr',
  KXMLBRBI: 'rbi',
  KXMLBTB: 'tb',
  KXMLBSB: 'sb',
  KXMLBKS: 'ks',
  KXMLBOUTS: 'outs',
  KXMLBHRR: 'hrr', // hits + runs + RBIs
};

/** Display order + labels for the Player Props stat chips. */
export const MLB_PROP_STATS: readonly MlbPropStat[] = [
  'hits', 'hr', 'rbi', 'tb', 'sb', 'ks', 'outs', 'hrr',
];

export const MLB_PROP_STAT_LABELS: Record<MlbPropStat, string> = {
  hits: 'Hits',
  hr: 'Home Runs',
  rbi: 'RBIs',
  tb: 'Total Bases',
  sb: 'Stolen Bases',
  ks: 'Strikeouts',
  outs: 'Outs',
  hrr: 'H+R+RBI',
};

/** The two sides of ONE player-prop rung, worded from the same parts so they
 *  cannot drift. YES names the outcome ("Brandon Sproat (MIL) 6+ Strikeouts");
 *  NO is its complement.
 *
 *  Lives here, not at either call site, because BOTH GameDetail's Player Props
 *  tab and the Position Tracker panel mint this synth — a wording change has to
 *  land in one place, not two.
 *
 *  The complement deliberately repeats the market's own "N+" token instead of
 *  converting to "under N": the trader then reads the SAME threshold on both
 *  sides rather than doing the N vs N−1 boundary conversion in their head.
 *
 *  Poly-only ladders carry no team code — omit the "()" rather than print an
 *  empty one. */
export function mlbPropRungLabels(
  playerName: string,
  teamCode: string | null | undefined,
  stat: MlbPropStat,
  threshold: number,
): { label: string; notLabel: string } {
  const team = teamCode ? ` (${teamCode})` : '';
  const rung = `${threshold}+ ${MLB_PROP_STAT_LABELS[stat]}`;
  return {
    label: `${playerName}${team} ${rung}`,
    notLabel: `${playerName}${team} does not get ${rung}`,
  };
}

export const MLB_PROP_CATEGORIES: Record<MlbPropStat, TickerCategory> = {
  hits: 'mlb-hits',
  hr: 'mlb-hr',
  rbi: 'mlb-rbi',
  tb: 'mlb-tb',
  sb: 'mlb-sb',
  ks: 'mlb-ks',
  outs: 'mlb-outs',
  hrr: 'mlb-hrr',
};

const MLB_EVENT_SERIES: ReadonlySet<string> = new Set([
  'KXMLBTEAMTOTAL',
  'KXMLBRFI',
  'KXMLBEXTRAS',
]);

/** True when the ticker's series segment (exact match, before the first dash)
 *  is one of the 11 prop/event series. The cheap pre-gate callers run before
 *  classifyMlbExtendedTicker, so a structural-parse failure can be logged as
 *  an unrecognized variant instead of silently reading as "not a prop". */
export function isMlbExtendedSeriesTicker(ticker: string): boolean {
  const i = ticker.indexOf('-');
  const series = (i > 0 ? ticker.slice(0, i) : ticker).toUpperCase();
  return series in MLB_PROP_SERIES || MLB_EVENT_SERIES.has(series);
}

/** Structural classification of one MLB prop/event market ticker. */
export type MlbExtendedClass =
  | {
      kind: 'prop';
      stat: MlbPropStat;
      teamCode: string;
      playerId: string;
      jerseyNumber?: number;
      /** Yes-ladder threshold: YES resolves at N or more (floor N − 0.5). */
      threshold: number;
    }
  | { kind: 'teamtotal'; teamCode: string; threshold: number }
  | { kind: 'rfi' }
  | { kind: 'extras' };

/** Split a `<TEAM><REST>` blob on a KNOWN MLB team code (2–3 letters,
 *  registry-validated — MLB codes are variable length, so fixed offsets
 *  mis-split). Longest code wins (no current 2-char code prefixes a 3-char
 *  one, so the order is convention, not correctness). Null when no known
 *  code leads the blob — callers fail-safe. */
function splitKnownMlbTeamPrefix(blob: string): [string, string] | null {
  for (const len of [3, 2]) {
    const code = blob.slice(0, len);
    if (code.length === len && mlbTeamFromCode(code) && blob.length > len) {
      return [code, blob.slice(len)];
    }
  }
  return null;
}

/** Split a concatenated `<AWAY><HOME>` blob where BOTH halves must be known
 *  MLB codes. Tries both away lengths so a valid 3-char lead with an invalid
 *  remainder still falls back to the 2-char split. */
function splitMlbTeamPair(blob: string): [string, string] | null {
  for (const len of [3, 2]) {
    const away = blob.slice(0, len);
    const home = blob.slice(len);
    if (away.length === len && home && mlbTeamFromCode(away) && mlbTeamFromCode(home)) {
      return [away, home];
    }
  }
  return null;
}

interface MlbGameCore {
  date: string;
  awayCode: string | null;
  homeCode: string | null;
  /** Doubleheader game number from the G1/G2 event-ticker suffix. */
  gameNumber?: 1 | 2;
}

/** Parse the shared game core `<YY><MON><DD><HHMM><AWAY><HOME>[G1|G2]`.
 *  Team split is registry-anchored (no outcome-team anchor exists on these
 *  tickers); unknown codes leave away/home null rather than guessing a
 *  midpoint split ('TBBOS' would mis-split). Null only when the date is
 *  unparseable. */
function parseMlbEventCore(core: string): MlbGameCore | null {
  if (core.length < 7) return null;
  const date = parseDateFromTicker(core.slice(0, 7));
  if (!date) return null;
  let blob = core.slice(7);
  if (/^\d{4}/.test(blob)) blob = blob.slice(4); // optional HHMM start time
  const dh = splitMlbDoubleheaderSuffix(blob);
  const pair = splitMlbTeamPair(dh.teams);
  return {
    date,
    awayCode: pair?.[0] ?? null,
    homeCode: pair?.[1] ?? null,
    gameNumber: dh.gameNumber,
  };
}

/**
 * Classify an MLB player-prop / event-market ticker by its EXACT series
 * segment (the text before the first dash) — startsWith would let KXMLBHR
 * shadow KXMLBHRR. Returns null for anything that isn't one of the 11 new
 * series or whose market segments don't match the published anatomy
 * (fail-safe: callers drop + log rather than guessing).
 */
export function classifyMlbExtendedTicker(ticker: string): MlbExtendedClass | null {
  const raw = ticker.trim().toUpperCase();
  const parts = raw.split('-');
  const series = parts[0] ?? '';

  const stat = MLB_PROP_SERIES[series];
  if (stat) {
    // <series>-<core>-<TEAM><PLAYERCODE>-<N>
    if (parts.length !== 4) return null;
    const split = splitKnownMlbTeamPrefix(parts[2] ?? '');
    if (!split) return null;
    const playerMatch = split[1].match(/^([A-Z]+)(\d+)?$/);
    if (!playerMatch) return null;
    const threshold = /^\d+$/.test(parts[3] ?? '') ? Number(parts[3]) : NaN;
    if (!Number.isFinite(threshold)) return null;
    return {
      kind: 'prop',
      stat,
      teamCode: split[0],
      playerId: playerMatch[1],
      jerseyNumber: playerMatch[2] ? Number(playerMatch[2]) : undefined,
      threshold,
    };
  }

  if (series === 'KXMLBTEAMTOTAL') {
    // <series>-<core>-<TEAM><N>
    if (parts.length !== 3) return null;
    const m = (parts[2] ?? '').match(/^([A-Z]+)(\d+)$/);
    if (!m || !mlbTeamFromCode(m[1])) return null;
    return { kind: 'teamtotal', teamCode: m[1], threshold: Number(m[2]) };
  }

  if (series === 'KXMLBRFI') {
    // Market ticker == event ticker — exactly two segments, a valid date core.
    if (parts.length !== 2 || !parseDateFromTicker((parts[1] ?? '').slice(0, 7))) return null;
    return { kind: 'rfi' };
  }

  if (series === 'KXMLBEXTRAS') {
    if (parts.length !== 3 || parts[2] !== 'EXTRAS') return null;
    return { kind: 'extras' };
  }

  return null;
}

/**
 * Display parser for all 11 MLB prop/event series (dispatched per prefix from
 * parse.ts). Builds the game from the shared event core; unknown team codes
 * leave `game` null (fail-safe — describeMarket falls back to the raw ticker
 * and logs) rather than mis-splitting the blob.
 */
export function parseMlbExtended(raw: string): ParsedTicker {
  const cls = classifyMlbExtendedTicker(raw);
  if (!cls) return parseFallback(raw);

  const parts = raw.split('-');
  const series = parts[0];
  const core = parseMlbEventCore(parts[1] ?? '');
  if (!core) return parseFallback(raw);

  const game: GameInfo | null =
    core.awayCode && core.homeCode
      ? {
          awayCode: core.awayCode,
          homeCode: core.homeCode,
          awayName: mlbTeamFromCode(core.awayCode) ?? undefined,
          homeName: mlbTeamFromCode(core.homeCode) ?? undefined,
          gameNumber: core.gameNumber,
        }
      : null;
  const dateLabel = formatDateShort(core.date);
  const gameLabel = game
    ? `${game.awayCode} @ ${game.homeCode}${core.gameNumber ? ` (G${core.gameNumber})` : ''}`
    : '';

  if (cls.kind === 'prop') {
    const player: PlayerInfo = {
      teamCode: cls.teamCode,
      playerId: cls.playerId,
      jerseyNumber: cls.jerseyNumber,
      displayName: formatPlayerName(cls.playerId, cls.jerseyNumber),
    };
    const propLabel = `${MLB_PROP_STAT_LABELS[cls.stat]} ${cls.threshold}+`;
    const playerLabel = player.displayName ?? player.playerId;
    return {
      raw,
      category: MLB_PROP_CATEGORIES[cls.stat],
      series,
      date: core.date,
      game,
      player,
      spread: null,
      total: null,
      tennis: null,
      outcome: `${cls.threshold}+`,
      shortLabel: `${propLabel}: ${playerLabel}`,
      fullLabel: [`MLB ${propLabel}`, dateLabel, gameLabel, playerLabel]
        .filter(Boolean)
        .join(' · '),
    };
  }

  if (cls.kind === 'teamtotal') {
    const teamName = mlbTeamFromCode(cls.teamCode) ?? cls.teamCode;
    return {
      raw,
      category: 'mlb-teamtotal',
      series,
      date: core.date,
      game,
      player: null,
      spread: null,
      // YES resolves at N or more runs — an Over (N − 0.5) line.
      total: { totalValue: cls.threshold - 0.5, side: 'over' },
      tennis: null,
      outcome: `${cls.teamCode}${cls.threshold}`,
      shortLabel: `Team Total: ${cls.teamCode} ${cls.threshold}+`,
      fullLabel: ['MLB Team Total', dateLabel, gameLabel, `${teamName} ${cls.threshold}+ runs`]
        .filter(Boolean)
        .join(' · '),
    };
  }

  const isRfi = cls.kind === 'rfi';
  return {
    raw,
    category: isRfi ? 'mlb-rfi' : 'mlb-extras',
    series,
    date: core.date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: isRfi ? 'YRFI' : 'EXTRAS',
    shortLabel: isRfi
      ? `YRFI: ${gameLabel || dateLabel}`
      : `Extra Innings: ${gameLabel || dateLabel}`,
    fullLabel: [isRfi ? 'MLB 1st Inning Run (YRFI)' : 'MLB Extra Innings', dateLabel, gameLabel]
      .filter(Boolean)
      .join(' · '),
  };
}
